
import { useState } from "react";
import { FileCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnalysisUploader } from "@/components/analysis";
import AnalysisResults, { ResultLevel } from "@/components/AnalysisResults";
import { analyzeDocument } from "@/services/fraudDetection";
import { toast } from "sonner";

const DocumentAnalysis = () => {
  const [results, setResults] = useState<{
    results: {
      type: string;
      level: ResultLevel;
      score: number;
      details: string;
    }[];
    overallStatus: ResultLevel;
  } | null>(null);
  
  const [isLoading, setIsLoading] = useState(false);

  const handleAnalyze = async (file: File | string) => {
    setIsLoading(true);
    
    return new Promise<void>((resolve, reject) => {
      try {
        setTimeout(() => {
          // Create a more realistic document analysis that favors genuine documents
          const fileIsDocument = file instanceof File && 
            /\.(pdf|doc|docx|xls|xlsx|ppt|pptx)$/i.test(file.name);
          
          // Bias toward identifying documents as genuine
          const integrityScore = fileIsDocument ? Math.random() * 25 : 30 + (Math.random() * 30);
          const signatureScore = fileIsDocument ? Math.random() * 30 : 40 + (Math.random() * 40);
          const metadataScore = fileIsDocument ? 10 + (Math.random() * 20) : 30 + (Math.random() * 40);
          const contentScore = fileIsDocument ? Math.random() * 15 : 20 + (Math.random() * 30);
          
          const getLevel = (score: number): ResultLevel => {
            if (score < 30) return "safe";
            if (score < 60) return "suspicious";
            return "fraudulent";
          };
          
          const mockResults = {
            results: [
              {
                type: "Document Integrity",
                level: getLevel(integrityScore),
                score: Math.round(integrityScore),
                details: integrityScore < 30 
                  ? "Document structure appears consistent and authentic."
                  : "Some inconsistencies in document formatting and structure detected.",
              },
              {
                type: "Signature Verification",
                level: getLevel(signatureScore),
                score: Math.round(signatureScore),
                details: signatureScore < 30 
                  ? "Digital signatures appear valid and consistent with document content."
                  : "Digital signature appears to have been manipulated or copied.",
              },
              {
                type: "Metadata Analysis",
                level: getLevel(metadataScore),
                score: Math.round(metadataScore),
                details: metadataScore < 30 
                  ? "Document metadata is consistent with claimed origin and editing history."
                  : "Document metadata shows editing history inconsistent with claimed origin.",
              },
              {
                type: "Content Consistency",
                level: getLevel(contentScore),
                score: Math.round(contentScore),
                details: contentScore < 30 
                  ? "Text content appears consistent throughout the document."
                  : "Some content appears to have been altered or modified.",
              },
            ],
            // Calculate overall status with bias toward genuine for typical document types
            overallStatus: fileIsDocument 
              ? (Math.random() > 0.8 ? "suspicious" : "safe") as ResultLevel
              : getLevel((integrityScore + signatureScore + metadataScore + contentScore) / 4),
          };
          
          setResults(mockResults);
          setIsLoading(false);
          
          if (mockResults.overallStatus === "safe") {
            toast.success("Document appears to be authentic");
          } else if (mockResults.overallStatus === "suspicious") {
            toast.warning("Document has some suspicious elements");
          } else {
            toast.error("Document appears to be fraudulent");
          }
          
          resolve();
        }, 2000);
      } catch (error) {
        console.error("Analysis error:", error);
        setIsLoading(false);
        reject(error);
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Document Analysis</h1>
        <p className="text-muted-foreground">
          Verify document authenticity and detect fraudulent modifications
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <AnalysisUploader
            title="Document Analysis"
            description="Upload a document to verify authenticity"
            acceptedFileTypes=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            icon={<FileCheck className="h-5 w-5 text-fraudeye-accent" />}
            onAnalyze={handleAnalyze}
            allowText={false}
          />

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
              <CardDescription>
                Our document analysis uses multiple verification techniques
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium">Document Integrity</h3>
                <p className="text-sm text-muted-foreground">
                  Examines format consistency, font variations, and page layout for signs of tampering.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Signature Verification</h3>
                <p className="text-sm text-muted-foreground">
                  Analyzes digital and scanned signatures for authenticity and possible manipulation.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Metadata Analysis</h3>
                <p className="text-sm text-muted-foreground">
                  Examines document creation, modification history, and author information for inconsistencies.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Content Consistency</h3>
                <p className="text-sm text-muted-foreground">
                  Checks for logical continuity, formatting consistency, and natural content flow.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          {results ? (
            <AnalysisResults {...results} />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileCheck className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">No Analysis Results Yet</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md">
                    Upload a document to begin analysis. Results will appear here once processing is complete.
                  </p>
                  <Alert variant="default" className="max-w-md border-fraudeye-accent/50 bg-fraudeye-accent/10">
                    <AlertTriangle className="h-4 w-4 text-fraudeye-accent" />
                    <AlertTitle>Tip</AlertTitle>
                    <AlertDescription>
                      Original documents rather than scanned copies provide the most accurate results.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Button className="w-full" variant="outline">
              Export Results
            </Button>
            <Button className="w-full bg-fraudeye-accent hover:bg-fraudeye-highlight">
              New Analysis
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalysis;
